import { peekFitnessGuidance } from '@/i18n/translations';
import { getExplicitFitnessMovement } from '@/lib/fitnessExerciseIdentity';
import { detectLangFromTags, normalizeLang } from '@/i18n/locale';

/** Original static instruction for the sixteen explicit upper-body variants.
 * Sources, declared adaptations and retrieval limits are recorded in
 * docs/research/fitness-rea-20261015/guidance-drafts/upper16/sources.json.
 * No camera observation, personalized assessment or professional review is implied.
 */
export const UPPER_EXERCISE_GUIDANCE_IDS = [
  "chin-up",
  "incline-barbell-bench-press",
  "decline-barbell-bench-press",
  "incline-dumbbell-bench-press",
  "incline-dumbbell-fly",
  "one-arm-supported-dumbbell-row",
  "standing-dumbbell-shoulder-press",
  "standing-barbell-curl",
  "standing-alternating-dumbbell-curl",
  "standing-dumbbell-hammer-curl",
  "standing-barbell-reverse-curl",
  "seated-incline-dumbbell-curl",
  "flat-barbell-skull-crusher",
  "supported-one-arm-dumbbell-kickback",
  "standing-one-arm-overhead-dumbbell-extension",
  "flat-dumbbell-bench-press"
] as const;
export type UpperExerciseGuidanceId = typeof UPPER_EXERCISE_GUIDANCE_IDS[number];
type GuidanceCopy = readonly [string, string, string, string, string];
export type UpperExerciseGuidanceDictionary = Record<UpperExerciseGuidanceId, GuidanceCopy>;
const supported = new Set<string>(UPPER_EXERCISE_GUIDANCE_IDS);

/** Exact app identity/canonical name; translated displays and provider IDs are not inferred. */
export function getUpperExerciseGuidance(exerciseName: string, lang?: string): { steps: string[]; cues: string[] } | null {
  const id = getExplicitFitnessMovement(exerciseName)?.id;
  if (!id || !supported.has(id)) return null;
  const language = normalizeLang(lang) ?? detectLangFromTags(lang ? [lang] : []) ?? 'EN';
  const copy = peekFitnessGuidance(language, 'upper', id);
  if (!copy) return null;
  return { steps: copy.slice(0, 3), cues: copy.slice(3) };
}
